import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateRecurringCost } from "../../hooks/realestate";
import type {
  CreateRecurringCostInput,
  RecurringCost,
  RecurringCostCategory,
  UpdateRecurringCostInput,
} from "../../lib/realestate";
import { RecurringCostFormModal } from "./RecurringCostFormModal";

const CATEGORIES: RecurringCostCategory[] = [
  "HAUSGELD",
  "VERSICHERUNG",
  "INSTANDHALTUNG",
  "GRUNDSTEUER",
  "SONSTIGES",
];

interface Props {
  propertyId: string;
  costs: RecurringCost[];
  onAdd: (input: CreateRecurringCostInput) => Promise<void>;
  addPending: boolean;
  onDelete: (costId: string) => void;
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

function findCurrentCost(
  costs: RecurringCost[],
  category: RecurringCostCategory,
): RecurringCost | null {
  const today = todayIso();
  const applicable = costs
    .filter((c) => c.category === category && c.effectiveFrom <= today)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
  return applicable[0] ?? null;
}

export function RecurringCostList({ propertyId, costs, onAdd, addPending, onDelete }: Props) {
  const { t } = useTranslation();
  const updateMutation = useUpdateRecurringCost();
  const [modalCategory, setModalCategory] = useState<RecurringCostCategory | null>(null);
  const [editingCost, setEditingCost] = useState<RecurringCost | null>(null);
  const [expanded, setExpanded] = useState<RecurringCostCategory | null>(null);

  const modalOpen = modalCategory !== null || editingCost !== null;

  function closeModal() {
    setModalCategory(null);
    setEditingCost(null);
  }

  return (
    <div className="space-y-3">
      {CATEGORIES.map((cat) => {
        const current = findCurrentCost(costs, cat);
        const categoryCosts = costs.filter((c) => c.category === cat);
        const isExpanded = expanded === cat;
        return (
          <div key={cat} className="bg-white border rounded p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">
                  {t(`realestate.recurring.category_label.${cat}`)}
                </p>
                <p className="text-lg font-mono mt-1">
                  {current
                    ? `${formatEur(current.monthlyAmountCents)}/Monat`
                    : t("realestate.recurring.not_set")}
                </p>
                {current && (
                  <p className="text-xs text-slate-500">
                    {t("realestate.recurring.since", { date: current.effectiveFrom })}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="text-sm px-3 py-1 border rounded"
                onClick={() => setModalCategory(cat)}
              >
                {current ? t("realestate.recurring.adjust") : t("realestate.recurring.add")}
              </button>
            </div>
            {categoryCosts.length > 0 && (
              <details
                open={isExpanded}
                onToggle={(e) => setExpanded((e.target as HTMLDetailsElement).open ? cat : null)}
                className="mt-2"
              >
                <summary className="cursor-pointer text-xs text-blue-600">
                  {t("realestate.recurring.show_history")} ({categoryCosts.length})
                </summary>
                <table className="w-full text-sm mt-2 border-collapse">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="p-1">{t("realestate.recurring.effective_from")}</th>
                      <th className="p-1 text-right">{t("realestate.recurring.monthly_amount")}</th>
                      <th className="p-1">{t("realestate.recurring.notes")}</th>
                      <th className="p-1" />
                    </tr>
                  </thead>
                  <tbody>
                    {[...categoryCosts]
                      .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))
                      .map((c) => (
                        <tr key={c.id} className="border-b">
                          <td className="p-1 font-mono text-xs">{c.effectiveFrom}</td>
                          <td className="p-1 text-right font-mono">
                            {formatEur(c.monthlyAmountCents)}
                          </td>
                          <td className="p-1 text-xs text-slate-600">{c.notes ?? "—"}</td>
                          <td className="p-1 text-right">
                            <button
                              type="button"
                              className="text-xs text-blue-600 underline mr-2"
                              onClick={() => setEditingCost(c)}
                            >
                              {t("realestate.recurring.edit_button")}
                            </button>
                            <button
                              type="button"
                              className="text-red-700 text-xs"
                              onClick={() => onDelete(c.id)}
                            >
                              {t("common.delete")}
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        );
      })}
      {modalOpen && (
        <RecurringCostFormModal
          open={modalOpen}
          defaultCategory={modalCategory ?? undefined}
          existing={editingCost}
          submitting={addPending || updateMutation.isPending}
          onClose={closeModal}
          onSubmit={async (input) => {
            if (editingCost) {
              await updateMutation.mutateAsync({
                propertyId,
                costId: editingCost.id,
                input: input as UpdateRecurringCostInput,
              });
              setEditingCost(null);
            } else {
              const createInput: CreateRecurringCostInput = {
                category: input.category as RecurringCostCategory,
                effectiveFrom: input.effectiveFrom as string,
                monthlyAmountCents: input.monthlyAmountCents as number,
                notes: input.notes ?? undefined,
              };
              await onAdd(createInput);
              setModalCategory(null);
            }
          }}
        />
      )}
    </div>
  );
}
